(function vorixHufIndicatorModule() {
  'use strict';

  const HU_HOSTS = new Set(['vorixgarage.hu', 'www.vorixgarage.hu']);
  const RATE_ENDPOINT = 'https://open.er-api.com/v6/latest/EUR';
  const ATTRIBUTION_URL = 'https://www.exchangerate-api.com';
  const CACHE_KEY = 'vorix:eur-huf-daily-rate:v1';
  const FETCH_TIMEOUT_MS = 7000;
  const FALLBACK_CACHE_MAX_AGE_MS = 30 * 60 * 60 * 1000;
  const MIN_PLAUSIBLE_RATE = 250;
  const MAX_PLAUSIBLE_RATE = 650;

  function isHungarianHost(hostname) {
    return HU_HOSTS.has(String(hostname || '').trim().toLowerCase());
  }

  function roundInformativeHuf(value) {
    if (!Number.isFinite(value) || value < 0) return null;
    const step = value < 100000 ? 10 : 100;
    return Math.round(value / step) * step;
  }

  function formatHuf(value) {
    if (!Number.isFinite(value)) return '';
    return new Intl.NumberFormat('hu-HU', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      useGrouping: true,
    })
      .format(value)
      .replace(/[\u00a0\u202f]/g, ' ');
  }

  function isValidRate(rate) {
    return (
      Number.isFinite(rate) &&
      rate >= MIN_PLAUSIBLE_RATE &&
      rate <= MAX_PLAUSIBLE_RATE
    );
  }

  // A tiszta fuggvenyek helyi Node-teszthez exportalhatok; bongeszoben ez az ag nem fut.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      isHungarianHost,
      roundInformativeHuf,
      formatHuf,
      isValidRate,
    };
  }

  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    !isHungarianHost(window.location.hostname)
  ) {
    return;
  }

  let activeRate = null;
  let activeRateUpdatedAt = null;
  let renderQueued = false;

  function installPreviewStyles() {
    if (document.getElementById('vorix-huf-indicator-styles')) return;

    const style = document.createElement('style');
    style.id = 'vorix-huf-indicator-styles';
    style.textContent = `
      .vorix-huf-indicator {
        display: block;
        margin: 8px 0;
        color: #E5E7EB;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.45;
      }
      .vorix-huf-indicator__amount {
        color: #FBBF24;
        font-weight: 700;
        letter-spacing: 0.01em;
      }
      .vorix-huf-indicator--cart,
      .vorix-huf-indicator--shipping {
        text-align: right;
      }
      .vorix-huf-indicator__source {
        display: inline-block;
        margin-left: 0.45rem;
        color: rgba(229, 231, 235, 0.55);
        font-size: 11px;
        font-weight: 400;
        text-decoration: underline;
        text-underline-offset: 0.15em;
      }
    `;
    document.head.appendChild(style);
  }

  function readCache() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(CACHE_KEY) || 'null');
      if (!parsed || !isValidRate(Number(parsed.rate))) return null;

      const fetchedAt = Number(parsed.fetchedAt);
      if (!Number.isFinite(fetchedAt)) return null;
      if (Date.now() - fetchedAt > FALLBACK_CACHE_MAX_AGE_MS) return null;

      return {
        rate: Number(parsed.rate),
        fetchedAt,
        updatedAt: Number(parsed.updatedAt) || fetchedAt,
      };
    } catch (_error) {
      return null;
    }
  }

  function writeCache(rate, payload) {
    try {
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          rate,
          fetchedAt: Date.now(),
          updatedAt: Number(payload.time_last_update_unix) * 1000 || Date.now(),
          nextUpdateAt: Number(payload.time_next_update_unix) * 1000 || null,
        })
      );
    } catch (_error) {
      // A privat bongeszes vagy blokkolt storage nem akadalyozhatja az ar megjeleniteset.
    }
  }

  function setIndicatorContent(element, hufAmount) {
    const prefix = document.createElement('span');
    prefix.textContent = '(Az ár Forintban, középárfolyamon: ';

    const amount = document.createElement('span');
    amount.className = 'vorix-huf-indicator__amount';
    amount.textContent = `${formatHuf(hufAmount)} Ft`;

    const suffix = document.createElement('span');
    suffix.textContent = ').';

    const source = document.createElement('a');
    source.className = 'vorix-huf-indicator__source';
    source.href = ATTRIBUTION_URL;
    source.target = '_blank';
    source.rel = 'noopener noreferrer';
    source.textContent = 'Árfolyamforrás';

    element.replaceChildren(prefix, amount, suffix, source);
    element.hidden = false;
    element.title = activeRateUpdatedAt
      ? `Tájékoztató EUR/HUF középárfolyam: ${activeRate.toFixed(4)}; frissítve: ${new Date(
          activeRateUpdatedAt
        ).toLocaleString('hu-HU')}`
      : `Tájékoztató EUR/HUF középárfolyam: ${activeRate.toFixed(4)}`;
  }

  function renderIndicator(element) {
    if (!(element instanceof HTMLElement) || !isValidRate(activeRate)) return;

    const eurCents = Number(element.dataset.hufEurCents);
    if (!Number.isSafeInteger(eurCents) || eurCents < 0) {
      element.hidden = true;
      return;
    }

    const roundedHuf = roundInformativeHuf((eurCents / 100) * activeRate);
    if (roundedHuf === null) {
      element.hidden = true;
      return;
    }

    setIndicatorContent(element, roundedHuf);
  }

  function renderAllIndicators() {
    if (!isValidRate(activeRate)) return;
    document.querySelectorAll('.vorix-huf-indicator[data-huf-eur-cents]').forEach(renderIndicator);
  }

  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    window.requestAnimationFrame(() => {
      renderQueued = false;
      renderAllIndicators();
    });
  }

  async function fetchDailyRate() {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const response = await window.fetch(RATE_ENDPOINT, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'default',
        referrerPolicy: 'no-referrer',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Rate API HTTP ${response.status}`);
      const payload = await response.json();
      const rate = Number(payload && payload.rates && payload.rates.HUF);

      if (payload.result !== 'success' || payload.base_code !== 'EUR' || !isValidRate(rate)) {
        throw new Error('Invalid EUR/HUF rate payload');
      }

      writeCache(rate, payload);
      return {
        rate,
        updatedAt: Number(payload.time_last_update_unix) * 1000 || Date.now(),
      };
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  function applyRate(rateRecord) {
    if (!rateRecord || !isValidRate(Number(rateRecord.rate))) return;
    activeRate = Number(rateRecord.rate);
    activeRateUpdatedAt = Number(rateRecord.updatedAt) || Date.now();
    installPreviewStyles();
    queueRender();
  }

  async function start() {
    // Gyors elso rajz ervenyes cache-bol, majd minden oldalbetolteskor friss API-keres.
    const cached = readCache();
    if (cached) applyRate(cached);

    try {
      applyRate(await fetchDailyRate());
    } catch (_error) {
      // Ha nincs ervenyes cache sem, a HUF tajekoztato rejtve marad.
      if (!cached) return;
    }

    // Dawn variansvaltas es AJAX-kosar uj DOM-elemeket hoz letre; csak az uj markereket rajzoljuk ujra.
    const observer = new MutationObserver(queueRender);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
